/**
 * ProductDetailDialog — click a product-overview card → this dialog opens
 * with an enlarged photo (3-4× the card thumbnail), item_number, photo_type
 * and description.
 *
 * Actions:
 *   • AI 抠图 (去背景)  → POST /remove-bg          (Gemini nano-banana → white JPEG)
 *   • 恢复原图           → POST /restore-bg         (revert to pre-抠图 photo)
 *   • AI 生成描述         → POST /generate-description (Gemini 3.1-pro vision, EN)
 *   • 编辑 / 保存         → PATCH /                   (edit item_number/description/…)
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Scissors, Undo2, Sparkles, Pencil, Save, X, Loader2, Package as PackageIcon } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const backendUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}${path}`;
};

// Only these keys are editable — mirrors backend _EDITABLE_FIELDS.
const EDITABLE_KEYS = ["item_number", "description", "category", "buyer", "photo_type"];

export function ProductDetailDialog({ row, open, onOpenChange, onUpdated }) {
  const { t } = useLanguage();
  const { user } = useAuth();
  // 2026-06 · 产品经理 (production_manager) gets read-only access here.
  const canEdit = user?.role !== "production_manager";
  const [current, setCurrent] = useState(row);
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(null); // 'bg' | 'restore' | 'desc' | 'save' | null

  // Reset local state whenever the parent hands us a new row (opening a
  // different card must NOT carry over the previous card's edits).
  useEffect(() => {
    setCurrent(row);
    setEditMode(false);
    setDraft({});
  }, [row]);

  const commit = useCallback((next) => {
    setCurrent(next);
    onUpdated?.(next);
  }, [onUpdated]);

  const call = async (path, method = "post", body) => {
    if (!current?.id) return;
    try {
      const { data } = await api.request({
        url: `/product-overview/${current.id}${path}`,
        method,
        data: body,
        timeout: 180_000,
      });
      commit(data);
      return data;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("po.detail.actionFailed") || "操作失败");
      throw e;
    }
  };

  const removeBg = async () => {
    setBusy("bg");
    try { await call("/remove-bg"); toast.success(t("po.detail.bgRemoved") || "背景已去除"); }
    catch {} finally { setBusy(null); }
  };

  const restore = async () => {
    setBusy("restore");
    try { await call("/restore-bg"); toast.success(t("po.detail.restored") || "已恢复原图"); }
    catch {} finally { setBusy(null); }
  };

  const generateDesc = async () => {
    setBusy("desc");
    try {
      const next = await call("/generate-description");
      if (next?.description) toast.success(t("po.detail.descGenerated") || "已生成产品描述");
    } catch {} finally { setBusy(null); }
  };

  const startEdit = () => {
    setDraft(Object.fromEntries(EDITABLE_KEYS.map((k) => [k, current?.[k] || ""])));
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setDraft({});
  };

  const save = async () => {
    // Only send fields the user actually changed to keep the audit trail clean.
    const changed = {};
    for (const k of EDITABLE_KEYS) {
      if ((draft[k] || "") !== (current?.[k] || "")) changed[k] = draft[k] || "";
    }
    if (Object.keys(changed).length === 0) { cancelEdit(); return; }
    setBusy("save");
    try {
      await call("", "patch", changed);
      toast.success(t("po.detail.saved") || "已保存");
      setEditMode(false);
    } catch {} finally { setBusy(null); }
  };

  if (!current) return null;
  const src = backendUrl(current.photo_url);
  const anyBusy = busy !== null;
  const descriptionEmpty = !(current.description || "").trim();
  const hasOriginal = !!current.original_photo_url;

  return (
    <Dialog open={open} onOpenChange={(o) => !anyBusy && onOpenChange(o)}>
      <DialogContent className="max-w-4xl" data-testid="po-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-lg">{current.item_number || "—"}</DialogTitle>
          <DialogDescription className="text-[11px]">
            {current.date ? `${t("po.date") || "日期"}: ${current.date}` : ""}
            {current.source === "webapp" ? ` · ${t("po.detail.webappOrigin") || "WebApp 上传"}` : ""}
            {current.bg_removed_at ? ` · ${t("po.detail.bgRemovedTag") || "已抠图"}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-6">
          {/* Enlarged image — checker background makes any residual transparency
              obvious. After 抠图 the image is flattened to white JPEG so it
              will appear as a solid white subject on this checker pattern. */}
          <div className="relative bg-[repeating-conic-gradient(#f0f0f0_0%_25%,transparent_0%_50%)] bg-[length:20px_20px] border border-border rounded-md overflow-hidden aspect-square" data-testid="po-detail-image">
            {src ? (
              <img src={src} alt={current.item_number} className="w-full h-full object-contain" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                <PackageIcon size={64} />
              </div>
            )}
            {anyBusy && (
              <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center gap-2 text-white">
                <Loader2 size={28} className="animate-spin" />
                <span className="text-xs">
                  {busy === "bg" && (t("po.detail.processing") || "AI 处理中…")}
                  {busy === "desc" && (t("po.detail.descLoading") || "AI 生成描述中…")}
                  {busy === "restore" && (t("po.detail.restoring") || "恢复中…")}
                  {busy === "save" && (t("po.detail.saving") || "保存中…")}
                </span>
              </div>
            )}
          </div>

          {/* Metadata pane — read-only OR edit mode. */}
          {!editMode ? (
            <dl className="space-y-3 text-sm" data-testid="po-detail-meta">
              <MetaRow label={t("po.detail.itemNumber") || "产品编号"} value={current.item_number} mono />
              <MetaRow label={t("po.detail.photoType") || "采购图片类型"} value={current.photo_type} />
              <MetaRow label={t("po.buyer") || "采购"} value={current.buyer} />
              <MetaRow label={t("po.category") || "分类"} value={current.category} />
              <div>
                <dt className="text-[11px] uppercase text-muted-foreground tracking-wider mb-1 flex items-center gap-2">
                  {t("po.detail.description") || "产品描述"}
                  {descriptionEmpty && (
                    <Button size="xs" variant="outline" onClick={generateDesc} disabled={anyBusy}
                            data-testid="po-detail-gen-desc" className="h-6 px-2 text-[10px]">
                      <Sparkles size={11} /> {t("po.detail.genDesc") || "AI 生成描述"}
                    </Button>
                  )}
                </dt>
                <dd className="text-sm whitespace-pre-wrap leading-relaxed" data-testid="po-detail-description">
                  {current.description || <span className="text-muted-foreground italic">{t("po.detail.noDescription") || "暂无描述"}</span>}
                </dd>
              </div>
            </dl>
          ) : (
            <div className="space-y-3 text-sm" data-testid="po-detail-edit-form">
              <EditRow label={t("po.detail.itemNumber") || "产品编号"} value={draft.item_number}
                       onChange={(v) => setDraft((p) => ({ ...p, item_number: v }))}
                       testid="po-edit-item-number" mono />
              <EditRow label={t("po.detail.photoType") || "采购图片类型"} value={draft.photo_type}
                       onChange={(v) => setDraft((p) => ({ ...p, photo_type: v }))}
                       testid="po-edit-photo-type" />
              <EditRow label={t("po.buyer") || "采购"} value={draft.buyer}
                       onChange={(v) => setDraft((p) => ({ ...p, buyer: v }))}
                       testid="po-edit-buyer" />
              <EditRow label={t("po.category") || "分类"} value={draft.category}
                       onChange={(v) => setDraft((p) => ({ ...p, category: v }))}
                       testid="po-edit-category" />
              <div>
                <label className="text-[11px] uppercase text-muted-foreground tracking-wider">
                  {t("po.detail.description") || "产品描述"}
                </label>
                <Textarea
                  value={draft.description || ""}
                  onChange={(e) => setDraft((p) => ({ ...p, description: e.target.value }))}
                  rows={4}
                  className="mt-1 text-sm"
                  data-testid="po-edit-description"
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="justify-between sm:justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {!editMode ? (
              <>
                {canEdit && <>
                <Button variant="secondary" onClick={removeBg} disabled={anyBusy || !src}
                        data-testid="po-detail-remove-bg">
                  {busy === "bg" ? <Loader2 size={14} className="animate-spin" /> : <Scissors size={14} />}
                  {busy === "bg" ? (t("po.detail.processing") || "AI 处理中…") : (t("po.detail.removeBg") || "AI 抠图 (去背景)")}
                </Button>
                {hasOriginal && (
                  <Button variant="outline" onClick={restore} disabled={anyBusy}
                          data-testid="po-detail-restore-bg">
                    {busy === "restore" ? <Loader2 size={14} className="animate-spin" /> : <Undo2 size={14} />}
                    {t("po.detail.restore") || "恢复原图"}
                  </Button>
                )}
                <Button variant="outline" onClick={startEdit} disabled={anyBusy} data-testid="po-detail-edit">
                  <Pencil size={14} /> {t("po.detail.edit") || "编辑"}
                </Button>
                </>}
              </>
            ) : (
              <>
                <Button variant="default" onClick={save} disabled={anyBusy} data-testid="po-detail-save">
                  {busy === "save" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  {t("po.detail.save") || "保存"}
                </Button>
                <Button variant="ghost" onClick={cancelEdit} disabled={anyBusy} data-testid="po-detail-cancel">
                  <X size={14} /> {t("common.cancel") || "取消"}
                </Button>
              </>
            )}
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={anyBusy} data-testid="po-detail-close">
            {t("common.close") || "关闭"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MetaRow({ label, value, mono }) {
  return (
    <div>
      <dt className="text-[11px] uppercase text-muted-foreground tracking-wider">{label}</dt>
      <dd className={`text-sm ${mono ? "font-mono font-semibold" : ""}`}>{value || "—"}</dd>
    </div>
  );
}

function EditRow({ label, value, onChange, testid, mono }) {
  return (
    <div>
      <label className="text-[11px] uppercase text-muted-foreground tracking-wider">{label}</label>
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1 h-8 text-sm ${mono ? "font-mono" : ""}`}
        data-testid={testid}
      />
    </div>
  );
}
